/**
 * Sorted IANA time zone identifiers for learner timezone pickers.
 * Prefers Intl.supportedValuesOf('timeZone') when available.
 */

const FALLBACK_IANA_ZONES: readonly string[] = [
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'America/Anchorage',
  'America/Bogota',
  'America/Buenos_Aires',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Baghdad',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Kuwait',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Asia/Tashkent',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Pacific/Auckland',
  'UTC'
];

let cachedZones: string[] | null = null;

export function getAllIanaTimeZones(): string[] {
  if (cachedZones) return cachedZones;
  try {
    const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf;
    if (typeof supportedValuesOf === 'function') {
      cachedZones = supportedValuesOf('timeZone').slice().sort((a, b) => a.localeCompare(b));
      return cachedZones;
    }
  } catch {
    // ignore
  }
  cachedZones = [...FALLBACK_IANA_ZONES].sort((a, b) => a.localeCompare(b));
  return cachedZones;
}

export function filterTimeZones(query: string, zones: string[], limit = 200): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return zones.slice(0, limit);
  const hit = zones.filter((z) => z.toLowerCase().includes(q));
  return hit.slice(0, limit);
}

/**
 * Returns the user's device IANA time zone (e.g. "Asia/Tehran") with a UTC fallback.
 * Safe to call in non-browser environments.
 */
export function getDeviceTimeZone(): string {
  try {
    if (typeof Intl !== 'undefined') {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (typeof tz === 'string' && tz.trim()) return tz.trim();
    }
  } catch {
    // ignore – fall through to UTC
  }
  return 'UTC';
}

/**
 * Ensures a particular IANA time zone string exists in the list of options.
 * If the value is missing or already present, returns the original list; otherwise
 * returns a new list with the value prepended so combo boxes can render it.
 */
export function ensureTimeZoneInList(tz: string | null | undefined, zones: readonly string[]): string[] {
  const value = typeof tz === 'string' ? tz.trim() : '';
  if (!value) return [...zones];
  if (zones.includes(value)) return [...zones];
  return [value, ...zones];
}
